import * as React from 'react';
import { useCallback, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import type { NodeInstanceDataPort } from '@glueapp/graph-types';
import type { ThemeType } from '@/theme/glueTheme';
import { useCurrentNodeInstance } from '@/GraphEditor/drawingLayer/contexts/CurrentNodeInstanceContext';
import { useMergeInGraph } from '@/GraphEditor/drawingLayer/contexts/MergeInGraphContext';
import { canBuildObjectForType, canUnpackFromType, getBuildObjectTargetType } from './portQuickActions';
import { createObjectForInPort, createUnpackFromOutPort } from './createQuickActionNode';

interface PortQuickActionFooterProps {
    port: NodeInstanceDataPort;
    side: 'in' | 'out';
}

export const PortQuickActionFooter: React.FC<PortQuickActionFooterProps> = ({ port, side }) => {
    const theme = useContext<ThemeType>(ThemeContext);
    const nodeInstance = useCurrentNodeInstance();
    const mergeInGraph = useMergeInGraph();

    const stopPropagation = useCallback((event: { stopPropagation: () => void }) => {
        event.stopPropagation();
    }, []);

    if (!mergeInGraph) return null;

    const action = ((): { label: string; onClick: () => void } | undefined => {
        if (side === 'in') {
            const targetObjectType = canBuildObjectForType(port.type) ? getBuildObjectTargetType(port.type) : undefined;
            if (!targetObjectType) return undefined;
            return {
                label: '→ Build Object',
                onClick: () =>
                    createObjectForInPort({
                        mergeInGraph,
                        targetNode: nodeInstance,
                        targetPortName: port.name,
                        targetObjectType,
                    }),
            };
        }
        if (!canUnpackFromType(port.type)) return undefined;
        return {
            label: 'Unpack →',
            onClick: () =>
                createUnpackFromOutPort({
                    mergeInGraph,
                    sourceNode: nodeInstance,
                    sourcePortName: port.name,
                    sourcePortType: port.type,
                }),
        };
    })();

    if (!action) return null;

    return (
        <button
            type="button"
            onPointerDown={stopPropagation}
            onClick={event => {
                event.stopPropagation();
                action.onClick();
            }}
            className="w-full mt-2 pt-2 text-xs font-medium rounded-md px-2 py-1.5 transition-colors cursor-pointer"
            style={{
                borderTop: `1px solid ${theme.separatorColor}`,
                color: theme.primaryContrastColor,
                backgroundColor: 'transparent',
            }}
            onMouseEnter={event => {
                event.currentTarget.style.backgroundColor = theme.separatorColor;
            }}
            onMouseLeave={event => {
                event.currentTarget.style.backgroundColor = 'transparent';
            }}
        >
            {action.label}
        </button>
    );
};
